import { createTheme, responsiveFontSizes } from "@mui/material/styles";

// Typography
const typographySettings = {
  fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
  fontSize: 14,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  allVariants: {
    fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
  },
};

// Template Palette
const templatePalette = {
  primary: {
    0: "#418FDE",
    1: "#195ab4",
    2: "#001E62",
    3: "#021748",
  },
  secondary: {
    0: "#7F7F7F",
    1: "#2F456C",
    2: "#08336C",
    3: "#011030",
  },
  info: {
    0: "#b3e5fc",
    1: "#4fc3f7",
    2: "#03a9f4",
    3: "#0277bd",
  },
  error: {
    0: "#FFD2D2",
    1: "#F55F73",
    2: "#D22D4B",
    3: "#8C0019",
  },
  warning: {
    0: "#F8E08E",
    1: "#FFC845",
    2: "#C6922D",
    3: "#91640F",
  },
  success: {
    0: "#7AE1BF",
    1: "#00C389",
    2: "#00965E",
    3: "#006848",
  },
};

// Dark Palette
const darkPallete = {
  0: "#7F7F7F",
  1: "#3F3F3F",
  2: "#000000",
};

// Light Theme
const lightThemeSettings = {
  palette: {
    mode: "light",
    background: {
      default: "#f4f4f5", // Light gray for the outer sidebar background
      paper: "#ffffff",   // White for the inner balloon content
    },
    primary: {
      light: templatePalette.primary[1],
      main: templatePalette.primary[2],
      dark: templatePalette.primary[3],
    },
    secondary: {
      light: templatePalette.secondary[1],
      main: templatePalette.secondary[2],
      dark: templatePalette.secondary[3],
    },
    secondaryDark: {
      light: darkPallete[0],
      main: darkPallete[1],
      dark: darkPallete[2],
    },
    contrast: {
      light: templatePalette.primary[1],
      main: templatePalette.primary[2],
      dark: templatePalette.primary[3],
    },
    info: {
      light: templatePalette.info[0],
      main: templatePalette.info[1],
      dark: templatePalette.info[2],
    },
    error: {
      light: templatePalette.error[0],
      main: templatePalette.error[1],
      dark: templatePalette.error[2],
    },
    warning: {
      light: templatePalette.warning[0],
      main: templatePalette.warning[1],
      dark: templatePalette.warning[2],
    },
    success: {
      light: templatePalette.success[0],
      main: templatePalette.success[1],
      dark: templatePalette.success[2],
    },
    text: {
      primary: "#000000",
      secondary: "#333333",
    },
  },
  typography: typographySettings,
};

// Dark Theme (Zinc Monocromático)
let darkThemeSettings = {
  palette: {
    mode: "dark",
    background: {
      default: "#09090b", // Fundo preto neutro absoluto (Zinc 950)
      paper: "#121215",   // Card da aplicação / Tabela (Zinc 900 / Nível 1)
    },
    primary: {
      light: "#60a5fa",
      main: "#2563eb",    // Azul vibrante de ação (bg-blue-600)
      dark: "#1d4ed8",
      contrastText: "#ffffff",
    },
    secondary: {
      light: "#a1a1aa",   // Zinc 400
      main: "#71717a",    // Zinc 500
      dark: "#27272a",    // Zinc 800
      contrastText: "#fafafa",
    },
    secondaryDark: {
      light: "#a1a1aa",
      main: "#27272a",
      dark: "#09090b",
    },
    contrast: {
      light: "#FFFFFF",
      main: "#2563eb",
      dark: "#fafafa",
    },
    info: {
      light: templatePalette.info[3],
      main: templatePalette.info[2],
      dark: templatePalette.info[1],
    },
    error: {
      light: templatePalette.error[3],
      main: templatePalette.error[2],
      dark: templatePalette.error[1],
    },
    warning: {
      light: templatePalette.warning[3],
      main: templatePalette.warning[2],
      dark: templatePalette.warning[1],
    },
    success: {
      light: templatePalette.success[3],
      main: templatePalette.success[2],
      dark: templatePalette.success[1],
    },
    divider: "#27272a",   // Zinc 800
    text: {
      primary: "#fafafa",   // Zinc 50 / 100 (Branco puro neutro)
      secondary: "#a1a1aa", // Zinc 400 (Cinza claro neutro)
    },
    action: {
      active: "#fafafa",
      hover: "rgba(255, 255, 255, 0.04)",
      selected: "rgba(255, 255, 255, 0.08)",
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
  },
  typography: typographySettings,
};

// Telas de notebook (com o menu lateral aberto sobra pouca largura): células mais justas para as tabelas caberem
const tableCellOverride = {
  styleOverrides: {
    root: { "@media (max-width: 1439.95px)": { paddingLeft: 8, paddingRight: 8 } },
  },
};
// Celular/tablet: a seta invisível de coluna não ordenada ocupa espaço (no toque não há hover para mostrá-la)
const tableSortLabelOverride = {
  styleOverrides: {
    root: { "@media (max-width: 899.95px)": { "&:not(.Mui-active) .MuiTableSortLabel-icon": { display: "none" } } },
  },
};
// Toque: a dica abre com um toque simples (o padrão exige segurar 0,7s, e quase ninguém descobre). No mouse nada muda.
const tooltipOverride = { defaultProps: { enterTouchDelay: 0, leaveTouchDelay: 3000 } };
lightThemeSettings.components = { MuiTableCell: tableCellOverride, MuiTableSortLabel: tableSortLabelOverride, MuiTooltip: tooltipOverride };
darkThemeSettings.components.MuiTableCell = tableCellOverride;
darkThemeSettings.components.MuiTableSortLabel = tableSortLabelOverride;
darkThemeSettings.components.MuiTooltip = tooltipOverride;

// Default Theme (Light)
let lightTheme = createTheme(lightThemeSettings);
lightTheme = responsiveFontSizes(lightTheme);
export const theme = lightTheme;

// Dark Theme
let _darkTheme = createTheme(darkThemeSettings);
_darkTheme = responsiveFontSizes(_darkTheme);
export const darkTheme = _darkTheme;
